import {
  API_KEY,
  URL,
  MapParam,
  ANIMATION_DURATION,
  EventType,
  MediaRuleType
} from "./const.js";

import {debounce} from "./../../utils/debounce.js";

export default class YandexMap {
  constructor(container, textWrapper, mediaRules) {
    this._apiKey = API_KEY;
    this._url = URL;
    this._mediaRules = mediaRules;

    this._zoom = MapParam.ZOOM;
    this._iconUrl = MapParam.ICON.URL;
    this._iconSize = MapParam.ICON.SIZE;

    this._ymaps = null;
    this._map = null;

    this._coords = {
      latitude: null,
      longitude: null
    };

    this._document = document;
    this._container = container;
    this._textWrapper = textWrapper;

    this._renderMap = this._renderMap.bind(this);
    this._windowResizeHandler = this._windowResizeHandler.bind(this);
    this._windowOrientationChangeHandler = this._windowOrientationChangeHandler.bind(this);
  }

  init(coords) {
    this._coords = coords;
    this._loadScript(this._renderMap);
  }

  _loadScript(callback) {
    const observerOptions = {
      root: null,
      rootMargin: `500px`
    };

    const observerCallback = (entries, observer) => {
      let isIntersecting = false;

      entries.forEach((entry) => {
        if (isIntersecting === true) {
          return;
        }

        isIntersecting = entry.isIntersecting;
      });

      if (isIntersecting === false) {
        return;
      }

      observer.unobserve(this._container);

      const scriptElement = document.createElement(`script`);
      scriptElement.src = `${this._url}?apikey=${this._apiKey}&lang=ru_RU`;

      this._document.head.append(scriptElement);

      scriptElement.onload = callback;
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(this._container);
  }

  _renderMap() {
    // eslint-disable-next-line no-undef
    this._ymaps = ymaps;

    this._ymaps.ready(() => {
      this._map = new this._ymaps.Map(
          this._container,
          {
            center: [this._coords.latitude, this._coords.longitude],
            zoom: this._zoom,
            controls: {}
          },
          {
            suppressMapOpenBlock: true
          }
      );

      this._map.behaviors.disable([`scrollZoom`, `dblClickZoom`]);

      this._centerInPixels = this._map.getGlobalPixelCenter();

      this._setPlacemark();
      this._setMapCenterWithOffsets(EventType.INSTANT);
      this._setCopyrigtsStyle();
      this._setWindowResizeHandler();
    });
  }

  _setCopyrigtsStyle() {
    this._map.panes
      .get(`copyrights`)
      .getElement().style.display = `flex`;
  }

  _setPlacemark() {
    const placemark = new this._ymaps.Placemark(
        [this._coords.latitude, this._coords.longitude],
        {},
        {
          iconLayout: `default#image`,
          iconImageHref: this._iconUrl,
          iconImageSize: this._iconSize,
          iconOffset: [-this._iconSize[0] / 2, -this._iconSize[1] / 2]
        }
    );

    this._map.geoObjects
      .add(placemark);
  }

  _setMapCenterOffset({x, y}, type) {
    const newOffsetCoords = [
      this._centerInPixels[0] + (x || 0),
      this._centerInPixels[1] + (y || 0)
    ];

    const mapOptions = {};

    if (type !== EventType.INSTANT) {
      mapOptions.duration = ANIMATION_DURATION;
    }

    this._map.setGlobalPixelCenter(newOffsetCoords, this._zoom, mapOptions);
  }

  _setWindowResizeHandler() {
    window.addEventListener(`resize`, debounce(this._windowResizeHandler), {
      passive: true
    });
  }

  _setWindowOrientationChangeHandler() {
    window.addEventListener(`orientationchange`, debounce(this._windowOrientationChangeHandler));
  }

  _setMapCenterWithOffsets(type) {
    this._mediaRules.forEach((item) => {
      if (item.rule === MediaRuleType.DEFAULT) {
        this._setMapCenterOffset(item.offset(this._textWrapper), type);
        return;
      }

      if (window.matchMedia(item.rule).matches === true) {
        this._setMapCenterOffset(item.offset, type);
      }
    });
  }

  _windowResizeHandler() {
    this._setMapCenterWithOffsets();
  }

  _windowOrientationChangeHandler() {
    this._setMapCenterWithOffsets();
  }
}
